import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, logout as apiLogout, getMe } from '../api/auth';
import { apiFetchJSON } from '../api/client';

const FamilyAuthContext = createContext();

/**
 * Clear all auth tokens and session data
 * Called on login (to clear stale data) and logout
 */
const clearAuthTokens = () => {
  console.log('🧹 Clearing all auth tokens...');
  
  // Clear localStorage (device UUID should stay!)
  const deviceUUID = localStorage.getItem('hayat_device_uuid');
  localStorage.clear();
  if (deviceUUID) {
    localStorage.setItem('hayat_device_uuid', deviceUUID);
  }
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // Clear all cookies (by setting them to expired)
  document.cookie.split(";").forEach((c) => {
    document.cookie = c
      .replace(/^ +/, "")
      .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
  });
  
  console.log('✅ Auth tokens cleared');
};

/**
 * Family Auth Context Provider - Cookie-based authentication with robust token management
 * 
 * Features:
 * - Automatic token cleanup on login
 * - Session validation on app load
 * - 401/404 error handling
 * - Stale token detection
 */
export function FamilyAuthProvider({ children }) {
  // CRITICAL: Skip ENTIRE Provider on Device Domain
  const isDeviceDomain = 
    window.location.hostname === 'device.hayatfamily.app' || 
    window.location.pathname.startsWith('/device');
  
  const [familyUser, setFamilyUser] = useState(null);
  const [household, setHousehold] = useState(null);
  const [loading, setLoading] = useState(!isDeviceDomain); // Don't show loading on device
  const [sessionChecked, setSessionChecked] = useState(isDeviceDomain); // Skip check on device
  
  // CRITICAL: If on Device Domain, render children immediately WITHOUT any auth checks
  if (isDeviceDomain) {
    console.log('[FamilyAuth] 🚫 Device Domain detected - FamilyAuthProvider is DISABLED');
    // Return children directly, no auth logic
    return (
      <FamilyAuthContext.Provider value={{
        familyUser: null,
        household: null,
        loading: false,
        login: () => Promise.reject(new Error('Family Auth not available on Device Domain')),
        logout: () => Promise.reject(new Error('Family Auth not available on Device Domain')),
        refreshUser: () => Promise.reject(new Error('Family Auth not available on Device Domain'))
      }}>
        {children}
      </FamilyAuthContext.Provider>
    );
  }

  // Check session on mount - ONLY FOR FAMILY DOMAIN
  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('🔐 Validating session...');
        
        // Try to get user from session cookie
        const user = await getMe();
        
        if (user) {
          console.log('✅ Session valid:', user.email);
          setFamilyUser(user);
          
          // Load household if available
          if (user.household_id) {
            await loadHousehold(user.household_id);
          }
        } else {
          console.warn('⚠️ No valid session found');
          clearAuthTokens();
        }
      } catch (error) {
        console.error('❌ Session validation failed:', error);
        
        // If 401 or 404 - clear everything
        if (error?.response?.status === 401 || error?.response?.status === 404) {
          console.warn('⚠️ Session mismatch detected, refreshing auth state');
          clearAuthTokens();
          setFamilyUser(null);
          setHousehold(null);
        }
      } finally {
        setLoading(false);
        setSessionChecked(true);
      }
    };
    
    checkSession();
  }, []);

  // Load household data
  const loadHousehold = async (householdId) => {
    try {
      const household = await apiFetchJSON(`/api/households/${householdId}`);
      setHousehold(household);
    } catch (error) {
      console.error('Failed to load household:', error);
    }
  };

  // Login function - ROBUST VERSION with token cleanup
  const login = async (email, password) => {
    try {
      // STEP 1: Clear ALL old tokens/cookies BEFORE login
      console.log('🔐 Login: Clearing stale auth tokens...');
      clearAuthTokens();
      
      // STEP 2: Perform login
      const response = await apiLogin(email, password);
      
      // Response: {success: true, user: {...}}
      if (response.success && response.user) {
        const user = {
          id: response.user.user_id,
          name: response.user.name,
          email: response.user.email,
          household_id: response.user.household_id,
          role: response.user.role,
          has_completed_onboarding: response.user.has_completed_onboarding
        };
        
        setFamilyUser(user);
        console.log('✅ Login successful:', user.email);
        console.log('✅ New session established with fresh tokens');
        
        // Load household
        if (user.household_id) {
          await loadHousehold(user.household_id);
        }
        
        return user;
      }
      
      throw new Error('Invalid login response');
    } catch (error) {
      console.error('❌ Login failed:', error);
      clearAuthTokens(); // Clear tokens on failure too
      throw error;
    }
  };

  // Logout function - Cookie-based with full cleanup
  const logout = async () => {
    try {
      await apiLogout();
      console.log('✅ Logout API call successful');
    } catch (error) {
      console.error('⚠️ Logout API error:', error);
    } finally {
      // Always clear local state and tokens
      clearAuthTokens();
      setFamilyUser(null);
      setHousehold(null);
      console.log('✅ Logout complete, all tokens cleared');
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const user = await getMe();
      if (user) {
        setFamilyUser(user);
        
        if (user.household_id) {
          await loadHousehold(user.household_id);
        }
      }
    } catch (error) {
      console.error('Refresh failed:', error);
    }
  };

  // Badge label driven by currentUser.role from /me
  const isAdmin = familyUser?.role === 'admin';

  // Get authorization header for API calls (Cookie-based - no header needed)
  const getAuthHeader = () => {
    // Cookie-based authentication - no Authorization header needed
    // Cookies are automatically sent with requests
    return {};
  };

  const value = {
    familyUser,
    household,
    loading,
    login,
    logout,
    refreshUser,
    getAuthHeader,
    isAuthenticated: !!familyUser,
    isAdmin  // Role-based flag for UI
  };

  return (
    <FamilyAuthContext.Provider value={value}>
      {children}
    </FamilyAuthContext.Provider>
  );
}

export function useFamilyAuth() {
  const context = useContext(FamilyAuthContext);
  if (!context) {
    throw new Error('useFamilyAuth muss innerhalb von FamilyAuthProvider verwendet werden');
  }
  return context;
}
